<template>
    <v-layout ref="app" class="w-100">
        <!-- In Layout Template -->
        <!-- No circle, no app: a circle owns the devices, so everything
             behind here renders empty and every save posts to
             /companies/null/... A minor waiting to be added (circle_limbo) and
             an adult who belongs to no circle (circle_none) both land here and
             both leave the same way - a circle code - so it is one screen with
             copy that adapts. -->
        <template v-if="inCircleLimbo || hasNoCircle">
            <circle-limbo/>
        </template>
        <template v-else-if="circleSuspended && !onBillingRoute">
            <circle-suspended/>
        </template>
        <template v-else-if="restrictedMode">
            <Restricted/>
        </template>
        <template v-else>
            <sidebar v-if="!fullScreen"/>
            <top-bar v-if="!fullScreen"/>
            <payment-status-bar v-if="!fullScreen"/>
            <v-main>
                <slot></slot>
            </v-main>
            <app-footer v-if="!fullScreen"/>
            <payment-reminder-modal/>

            <v-btn
                :icon="`mdi-fullscreen${fullScreen?'-exit':''}`"
                class="btn-toggle-screen"
                size="small"
                @click="toggleScreen"
            />
        </template>
    </v-layout>
</template>

<script setup>
import {computed, ref, onMounted, onBeforeUnmount, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {storeToRefs} from "pinia";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useSubscriptionStore} from "@/stores/subscriptionStore";
import {useAssetTypeStore} from "@/stores/assetTypeStore";
import TopBar from "@/components/layouts/TopBar.vue";
import Sidebar from "@/components/layouts/Sidebar.vue";
import AppFooter from "@/components/layouts/AppFooter.vue";
import Restricted from "@/components/globals/restricted.vue";
import CircleLimbo from "@/components/globals/CircleLimbo.vue";
import CircleSuspended from "@/components/globals/CircleSuspended.vue";
import PaymentStatusBar from "@/components/layouts/PaymentStatusBar.vue";
import PaymentReminderModal from "@/components/dialogs/PaymentReminderModal.vue";
import {startCompanyReload, stopCompanyReload} from "@/composables/useCompanyReload";

const authStore = useAuthStore();
const assetTypeStore = useAssetTypeStore();
// storeToRefs keeps user/consents reactive - destructuring them directly
// froze the values at setup, so restrictedMode never recomputed and the
// consent gate never released after the user accepted all documents.
const {user, consents, inCircleLimbo, hasNoCircle, circleSuspended} = storeToRefs(authStore);
const subscriptionStore = useSubscriptionStore();
const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
let switchingCircle = false;

// A membership-change signal arrived over the reload socket (a join request was
// approved or an invitation accepted). If it targets THIS user they were just
// moved into a circle - switch the session into it and reload to re-scope all
// data. Anyone else in a shared circle just refreshes their data (new member
// appeared). Mirrors TopBar.switchCompany.
const onCircleMembershipChanged = (e) => {
    const action = e?.detail?.action;
    const targetUserId = e?.detail?.targetUserId;
    const cid = e?.detail?.companyId;
    const isMe = targetUserId && user.value?.id && Number(targetUserId) === Number(user.value.id);
    if (action === 'circle_membership_removed') {
        if (isMe) {
            // Name the circle for the banner BEFORE hydrate drops it from the list.
            const list = Array.isArray(user.value?.companies) ? user.value.companies : [];
            const left = list.find((c) => Number(c.id) === Number(cid));
            const msg = left?.company_name
                ? `You're no longer in ${left.company_name}`
                : "You're no longer in that circle";
            // We were removed: re-hydrate (the server heals our active company if
            // it was this circle). If the active circle actually changed we must
            // reload to re-scope everything - a snackbar wouldn't survive that, so
            // stash it and show it on the next mount. Otherwise show it now and
            // just refresh the (now-shorter) circle list + data.
            const before = companyId.value;
            authStore.hydrateProfile().finally(() => {
                if (Number(companyId.value) !== Number(before)) {
                    try { sessionStorage.setItem('circle_removed_msg', msg); } catch (e) { /* ignore */ }
                    router.go();
                } else {
                    showSnackbar(msg);
                    window.dispatchEvent(new CustomEvent('company-data-reload'));
                }
            });
        } else {
            window.dispatchEvent(new CustomEvent('company-data-reload'));
        }
        return;
    }
    if (!isMe || !cid || Number(cid) === Number(companyId.value)) {
        // Not us (or already here): a data refresh so a new member shows up.
        window.dispatchEvent(new CustomEvent('company-data-reload'));
        return;
    }
    if (switchingCircle) return;
    switchingCircle = true;
    send('post', `switch-company/${cid}`)
        .then((response) => {
            if (response.data.success) {
                authStore.updateAuthUser({...authStore.user, ...response.data.data});
                router.go();
            } else {
                switchingCircle = false;
            }
        })
        .catch(() => { switchingCircle = false; });
};
// Let a suspended owner still reach billing to pay - otherwise the full-screen
// suspended intercept traps them and the "Open billing to pay" CTA is dead.
const onBillingRoute = computed(() => ['plans', 'invoices', 'payment-methods'].includes(route.name));
const fullScreen = ref(false);
const restrictedMode = computed(() => {
    if (user.value?.type !== 'admin' && user.value?.company) {
        if (user.value?.company?.status === 'restricted' || consents.value) {
            return true;
        }
    }
    return false;
});

const toggleScreen = () => {
    fullScreen.value = !fullScreen.value;
};

// App-wide "company data changed" reload subscriber. Owned here (the authenticated
// shell) so config saves refresh the current page on EVERY route, not only Live.
// Restarts on a circle switch (company id changes); stops on logout/unmount.
const companyId = computed(() => user.value?.company?.id ?? null);
watch(companyId, (cid) => {
    if (cid) startCompanyReload(cid);
    else stopCompanyReload();
}, {immediate: true});

// Fallback for a join whose live MQTT signal couldn't reach us (we were scoped to
// a third circle we don't own, so it wasn't in the signal's reach, and web has no
// FCM): on load, if a circle appeared that wasn't in our last-seen set, switch
// into it - mirroring the app's autoSwitchIfNewlyJoined. Baseline is keyed by user
// id so a different user on this browser is never switched on the prior user's set.
const KNOWN_CIRCLES_KEY = 'sg_known_circle_ids';
const readKnownCircles = (uid) => {
    try {
        const raw = JSON.parse(localStorage.getItem(KNOWN_CIRCLES_KEY) || 'null');
        if (raw && Number(raw.userId) === Number(uid) && Array.isArray(raw.ids)) {
            return raw.ids.map(Number);
        }
    } catch (e) { /* ignore */ }
    return null; // no baseline for this user yet
};
const writeKnownCircles = (uid, ids) => {
    try {
        localStorage.setItem(KNOWN_CIRCLES_KEY, JSON.stringify({userId: uid, ids}));
    } catch (e) { /* ignore */ }
};
const autoSwitchIfNewlyJoined = () => {
    const uid = user.value?.id;
    if (!uid) return;
    const list = Array.isArray(user.value?.companies) ? user.value.companies : [];
    const current = list.map((c) => Number(c.id)).filter((n) => !Number.isNaN(n));
    if (!current.length) return;
    const known = readKnownCircles(uid);
    writeKnownCircles(uid, current);
    if (known === null) return; // first run for this user: baseline only, no switch
    const newly = current.filter((id) => !known.includes(id));
    if (!newly.length) return;
    const target = Math.max(...newly);
    if (Number(target) === Number(companyId.value) || switchingCircle) return;
    switchingCircle = true;
    send('post', `switch-company/${target}`)
        .then((response) => {
            if (response.data.success) {
                authStore.updateAuthUser({...authStore.user, ...response.data.data});
                router.go();
            } else {
                switchingCircle = false;
            }
        })
        .catch(() => { switchingCircle = false; });
};

onMounted(() => {
    // Show a "you're no longer in X" banner stashed by a removal that reloaded
    // the page to re-scope (a snackbar couldn't survive the reload).
    try {
        const removedMsg = sessionStorage.getItem('circle_removed_msg');
        if (removedMsg) {
            sessionStorage.removeItem('circle_removed_msg');
            showSnackbar(removedMsg);
        }
    } catch (e) { /* ignore */ }
    subscriptionStore.fetchIfStale();
    // Refresh the active circle + circle list so the switcher and owner-only
    // gating reflect the current server state on every load, then catch a join
    // that the live signal couldn't reach us for.
    Promise.resolve(authStore.hydrateProfile()).finally(autoSwitchIfNewlyJoined);
    // Load the consumer asset-type catalog so personas render table-driven.
    assetTypeStore.fetchConsumer();
    window.addEventListener('circle-membership-changed', onCircleMembershipChanged);
});

onBeforeUnmount(() => {
    stopCompanyReload();
    window.removeEventListener('circle-membership-changed', onCircleMembershipChanged);
});
</script>

<style scoped>
.btn-toggle-screen {
    right: 3%;
    bottom: 5%;
    z-index: 9999;
    position: fixed;
}
</style>